// ── WHAT WE ACTUALLY HOLD FOR A PRODUCTION REPORT ────────────────────────────
//
// Kevin, 2026-09-30: *"Maybe display all of the data we have for production and if we agree
// with it and know it's all correct, we can hit a button to fill production report, then
// that file management needs to be tracked."*
//
// This is the DISPLAY half, and it is deliberately the whole of this module: it assembles
// every channel side by side, names every disagreement, and decides NOTHING. The fill is a
// separate step a human takes after reading it — the same shape `api/_acreage.js` already
// proves, where `action=preview` returns a review object and stamps nothing.
//
// ⚠⚠ IT MUST NOT AVERAGE, RECONCILE OR PICK. Measured on the live CY2026 wheat book:
// KENNEDY FARMS reads 70.5 bu/ac on scale tickets and 46.4 on the John Deere rollup — a 52%
// gap on 390 acres. The likeliest cause is unprocessed maps (the check-jd-completeness
// failure mode: "not processed yet renders as FEWER ACRES, not as missing"), but *likeliest*
// is not a production record. A screen that split the difference would put a number on a
// federal form that no evidence supports. Two channels that disagree are TWO FACTS.
//
// ⚠ AND ABSENT IS NEVER ZERO. A county with no entry read as 0 is a total loss on that
// county — the largest indemnity a policy can pay. `sql/077` carries the same rule for
// `claim_county_production.bushels` and it is nullable for exactly this reason. Everything
// below distinguishes "" / null / absent from 0 all the way out.

"use strict";

const PR = require("./productionRecord.js");
// ⚠ THE UNIT RULE IS claimCrops.claimUnit AND IS NOT RE-DERIVED HERE. It already knows
// that corn silage (type 026) is TONS while corn grain is BUSHELS, and that potatoes are cwt,
// dry beans lbs, grapes ton. A second copy of a unit rule is how one surface comes to print
// tons under a bushels heading — the defect check-machine-vs-fsa exists for.
const { claimUnit: PR_UNIT, isSilageType } = require("./claimCrops.js");
// ⚠ THE ELEVATOR'S NET IS NOT THE PRODUCTION RMA COUNTS, AND THIS SCREEN IS ABOUT RMA.
// `grain_delivery_tickets.net_bushels` is the PAID basis — the elevator's dock, which bundles
// handling and drying with the water. RMA re-shrinks the GROSS at a %/pt above a base
// moisture and counts water only, so the two differ on essentially every farm. Measured over
// all 1,907 tickets in the book (every one of which carries gross AND moisture, so the
// fallback below never fires): **72 of 76 (farmer x crop x year) cells move**, and the
// direction runs BOTH WAYS — wheat up (ATWATER +1,034 bu, SULLIVAN +567, SILSBY +565), corn
// DOWN (SILSBY -1,165, JOHN B MARTIN -1,060). CY2026 wheat, the backlog this panel exists
// for, is +6,157 bu across 30 farms.
//
// Under-reporting wheat is bad; OVER-reporting corn is worse, because an inflated figure on
// the APH database raises the average the grower is measured against for ten years. So the
// lane quantity is the RMA basis.
//
// ⚠ AND IT IS THE SHARED RULE, NOT A SECOND COPY. `rmaNetFromTickets` has been the Claim
// Manager's own shrink since the screener shipped, and the block eight inches above this
// panel on screen already prints its answer ("Δ +370 bu — RMA counts more bushels"). Two
// copies of a shrink factor is how one screen comes to state a production figure the screen
// beside it contradicts — which is exactly what this panel did before this fix.
const { rmaNetFromTickets } = require("./claimEstimate.js");
const { pfUnitKey, apprUnitKey } = require("./unitKey.js");
const { harvestFormOf } = require("./harvestForm.js");

const n = (v) => {
  if (v == null || v === "") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
// Integer hundredths, so a figure a human will read off a screen and copy onto a form does
// not drift. `countyProduction.js` sums the same way.
const cents = (v) => Math.round((n(v) || 0) * 100);
const undo = (c) => Math.round(c) / 100;

// Base plans only. ⚠ AN SCO/ECO ROW RESTATES THE SAME PHYSICAL ACRES, so an acreage summed
// over crop_coverage runs 2-3x high — Anchor's Erie corn reads 1,527.76 against a real
// 763.88. Measured on CY2026 wheat: plan codes present are 01, 02, 32 and 88; the last two
// are the overlays.
const BASE_PLANS = new Set(["01", "02", "03", "90"]);
const isBasePlan = (c) => BASE_PLANS.has(String(c == null ? "" : c).padStart(2, "0"));

// ⚠ THE SAME TWO DEFAULTS `computeHarvestClaim` USES, so the panel and the claim block above
// it shrink identically when the farm has typed no override. They are RMA's conventional
// figures for grain, not a measurement of this book, and the screen prints them so a reader
// can see which factor produced the number rather than having to trust it.
const DEFAULT_SHRINK_PER_PT = 1.2;
const DEFAULT_BASE_MOISTURE = 13.5;
const ticketIsShrinkable = (t) => {
  const g = n(t.gross_bushels), m = n(t.moisture);
  return g != null && g > 0 && m != null;
};

/**
 * buildProductionEvidence({ crop, cropYear, coverages, policies, tickets, jdFtf,
 *                           appraisals, countyRows, inputs, aphRows, unit })
 *   -> a review object
 *
 * Every input is rows as they come out of Supabase. Nothing here fetches.
 */
/**
 * countyOverUnder({ guaranteeBu, plantedAcres, stated, appraised, jdQuantity, monitorComplete })
 *   -> { production, basis, over, under, ok } | { ok:false, why }
 *
 * Kevin, 2026-10-07, on the production-report table: *"it would be nice to add a bushel
 * guarantee and a dollar guarantee to this table, then it would all be in one place:
 * Insured acres, guarantees, what was appraised, are they under or over by county."*
 *
 * ⚠ THE COMPARISON IS THE WHOLE CLAIM SIGNAL, PER COUNTY, AND IT INHERITS THE DEFECT THE
 * FARM-LEVEL READ ALREADY PAID FOR. A production figure that covers PART of a county's
 * planted ground, compared against the WHOLE county's guarantee, values the unmeasured
 * acres at ZERO — which is the largest shortfall a policy can show. ROGERS FARMS read
 * "Likely claim — open it" with EST. INDEMNITY $459,938 off 308 appraised acres that were
 * themselves running 7% ABOVE their APH, because the bushels were divided across all 1,108
 * planted acres. This is that rule narrowed to one county.
 *
 * ⚠ AND THE DENOMINATOR IS PLANTED, NEVER INSURED. Insured includes prevent plant, and no
 * bushel was ever grown on a prevent-planted acre — so an appraisal covering every planted
 * acre of a county with PP would read as partial and the comparison would be withheld on a
 * county that is fully accounted for. MULLIGAN's LIVINGSTON is 1,183.16 insured, 27.70 PP,
 * 1,155.46 planted, and his adjuster walked 1,155.5.
 *
 * The ladder, strongest first. Each rung is a different KIND of answer, not a better
 * estimate of the same one:
 *   `stated`    — a figure a human accepted onto the claim. It is the answer whatever it
 *                 covers, because somebody has already decided it is the county's production.
 *   `appraised` — the adjuster's walk, used only where it covers the planted acres.
 *   `monitor`   — the combine, used only where `monitorComplete` (every insured field in the
 *                 county has a pass). That flag is computed upstream and is passed in rather
 *                 than re-derived, so the dash in the bu/ac column and the dash here can
 *                 never disagree.
 */
const OU_ACRE_TOL = 0.98;   // headlands and GPS gaps; the same tolerance the farm read uses
function countyOverUnder({ guaranteeBu, plantedAcres, stated, appraised,
                           jdQuantity, monitorComplete } = {}) {
  const g = n(guaranteeBu);
  if (!(g > 0)) return { ok: false, why: "no bushel guarantee on this county" };
  const planted = n(plantedAcres);

  let production = null, basis = null, partialOf = null;
  if (n(stated) != null) { production = n(stated); basis = "stated"; }
  else if (appraised && n(appraised.quantity) > 0) {
    const ac = n(appraised.acres) || 0;
    // ⚠ A COUNTY WITH NO PLANTED FIGURE CANNOT PROVE ITS APPRAISAL IS COMPLETE, so it is
    // refused rather than assumed complete — the direction that withholds a verdict.
    if (!(planted > 0)) return { ok: false, why: "no planted acreage to measure the appraisal against" };
    if (ac >= planted * OU_ACRE_TOL) { production = n(appraised.quantity); basis = "appraised"; }
    else { partialOf = { covered: ac, planted }; }
  }
  if (production == null && !partialOf && monitorComplete === true && n(jdQuantity) > 0) {
    production = n(jdQuantity); basis = "monitor";
  }
  if (production == null) {
    return partialOf
      ? { ok: false, why: `appraised on ${Math.round(partialOf.covered)} of ${Math.round(partialOf.planted)} planted ac — the rest would count as zero`,
          partial: partialOf }
      : { ok: false, why: "no production figure that covers this county" };
  }
  const diff = production - g;
  return {
    ok: true, production, basis, diff,
    // Named rather than left to a sign: a reader scanning a column wants the word.
    over: diff >= 0, under: diff < 0,
  };
}

/**
 * countyGuaranteeRead(countyRow, guaranteeByCounty) -> everything the county line shows
 *   about its guarantee and its over/under, in one object.
 *
 * ⚠ THIS EXISTS BECAUSE THE PANEL HAS TWO RENDERERS AND ONE OF THEM WAS LEFT BEHIND.
 * Kevin, 2026-10-07: *"Looks like the claims manager changes aren't on mobile."* He was
 * right. A phone gets ONE CARD PER COUNTY (added 2026-10-06, because eight columns
 * compressed to 390px shredded every heading) and the wide layout gets a table — and the
 * planted-acre switch, the bushel guarantee, the dollar guarantee, the bu/ac column and
 * the over/under all went into the TABLE only. The card still said "Insured 1,183.2 ac"
 * and carried none of them.
 *
 * That is this repo's most-recorded failure shape, and this panel has already paid for it
 * once: the SCO crop guard existed in 2 of 3 renderers, and the PDF printed insured acres
 * and blended liability where the screen printed planted acres and the planted portion.
 * The derivation was an inline IIFE inside one table row, so there was nowhere for the
 * second renderer to call it FROM — which is the actual defect. It is a function now, and
 * check-appraisal-county asserts both renderers call it.
 *
 * Every refusal is carried rather than collapsed, because the two layouts have different
 * room for a reason: the table puts it on a title attribute, the card can print it.
 */
function countyGuaranteeRead(c, guaranteeByCounty) {
  const g = (guaranteeByCounty && c && guaranteeByCounty[c.county]) || null;
  // ⚠ A COUNTY WHOSE PRICED COVERAGE DOES NOT COVER ITS ACRES SHOWS NO GUARANTEE AT ALL.
  // A total built from part of a county's acreage reads as the whole county's and makes the
  // comparison beside it wrong in the safe-looking direction — the production would clear a
  // guarantee that is too small. The test is ACRES, not a count of rows: an empty coverage
  // line proves nothing either way. 1% is two documents rounding, not a missing line.
  const insured = n(c && c.insuredAcres) || 0;
  const covers = !!g && n(g.guaranteedAcres) > 0 && n(g.guaranteedAcres) >= insured * 0.99;
  const gBu = covers ? n(g.bu) : null;
  const gTon = covers ? n(g.ton) : null;
  const liability = covers ? n(g.liability) : null;
  // PLANTED, not insured: no bushel comes off a prevent-planted acre, so an appraisal
  // covering every planted acre must not read as partial.
  const planted = c && c.plantedAcres != null ? n(c.plantedAcres)
    : (insured - (n(c && c.ppAcres) || 0));
  const ou = gBu > 0
    ? countyOverUnder({
        guaranteeBu: gBu,
        plantedAcres: (g && n(g.plantedAcres)) || planted,
        stated: c && c.stated, appraised: c && c.appraised,
        jdQuantity: c && c.jd ? c.jd.quantity : null,
        monitorComplete: c && c.monitorYield != null,
      })
    : { ok: false, why: g && !covers
        ? "the priced coverage lines do not cover this county's acreage"
        : "no guarantee on file for this county" };
  // The per-acre production is BLANK on exactly the rows where the comparison is withheld:
  // a per-acre figure off a part-county production is the same inversion in a smaller
  // number — Rogers' 308 appraised acres over 1,108 planted reads 54 bu/ac on a crop
  // running 196.
  const prodPerAcre = ou.ok && planted > 0.05 ? ou.production / planted : null;
  return {
    g, covers, gBu, gTon, liability, planted, ou, prodPerAcre,
    // Why the guarantee column is blank, in the words both layouts print.
    guaranteeWhy: g && !covers
      ? "the priced coverage lines in this county cover " + Math.round(n(g.guaranteedAcres) || 0).toLocaleString()
        + " of its " + Math.round(insured).toLocaleString()
        + " insured ac, so no county guarantee is shown — a part-acreage total would read as the whole county's"
      : null,
  };
}

/**
 * Appraised acres and quantity PER COUNTY, placed through the insured field map.
 *
 * Extracted 2026-10-06 so the Claim Check screen can offer an appraised figure in its
 * production-by-county boxes without growing a second copy of this join. Kevin, on seeing
 * those boxes read "not stated" above a rendered appraisal: *"Production by county says
 * not stated but it's literally in the appraisal."* He was right, and the naive version of
 * that fix — trusting `silage_appraisals.county` — is the very defect this rule corrects:
 * it is the worksheet HEADER's county, stamped onto every parsed row.
 *
 * All the reasoning for the rule itself sits at its one call site inside
 * buildProductionEvidence. The contract: FSN resolves through `policyFields`, a farm whose
 * insured ground spans two counties keeps its header county and is COUNTED as `ambiguous`
 * rather than split on no evidence, and every outcome is reported in `basis` so a rollup
 * that re-files acres can always say how many it moved and how many it could not place.
 */
// ── THE UNIT IS THE KEY, AND IT TIES OUT TO THE SOI EXACTLY ──────────────────
//
// An RMA unit is written in ONE county by construction, and the adjuster's worksheet
// prints the unit on every line (`unit_number`, e.g. "0002-0001-000"). The FSA farm is
// NOT county-specific — one farm number can hold insured ground in two counties, and
// that is the ordinary case rather than an oddity.
//
// MEASURED 2026-10-07 over every appraisal row in the book (81 rows, 3 farms):
//
//   key            resolved by unit   by FSA farm   fell back to header   county cells
//                                                                         over planted
//   FSA farm only          --              42               39                 1
//   UNIT first             77               4                0                 0
//
// MULLIGAN FAMILY FARM LLC is the case Kevin raised. His FSA farm 6198 holds 545.0
// Livingston-insured acres and 236.9 Monroe-insured acres, so the farm number cannot
// place it — but its worksheet lines carry units 0001-0001-000 and 0002-0001-000, and
// the eleven unit-0002 lines sum to **exactly 236.9**. Keyed on the unit his appraisal
// reads LIVINGSTON **1,155.5** / MONROE **377.8** against an NAU SOI of **1,155.46** /
// **377.79** planted — to the hundredth, with nothing falling back. Keyed on the farm
// it read LIVINGSTON 1,392.4 against 1,155.5 planted, which is the over-100% reading
// that started this.
//
// ⚠ ALL THREE RUNGS ARE LOAD-BEARING ON REAL DATA, so none of them is defensive:
//   · the UNIT places 77 of 81 rows (Mulligan 61, ROGERS FARMS 16)
//   · the FSA FARM places the other 4 — LANG FARMS LLC, whose unit column holds
//     "1.0000 BU" (a bushel figure the extractor put in the wrong column) AND whose
//     county is NULL on every row. Without this rung those 36.7 acres read
//     "(county not stated)"; with it they read ERIE.
//   · the HEADER places 0 rows today and stays as the last resort before
//     "(county not stated)".
function appraisalsByCounty({ appraisals = [], policyFields = [], cropYear = null } = {}) {
  // ⚠⚠ A UNIT NUMBER IS REUSED ACROSS CROP YEARS AND ITS COUNTY CAN CHANGE, so a field map
  // pooled over two years makes a unit look like it spans two counties when it never did.
  //
  // FOUND ON SCREEN, NOT BY READING, 2026-10-07. The rule was already correct and the
  // ProductionEvidencePanel still printed MULLIGAN as LIVINGSTON 1,438.8 / MONROE 94.5,
  // because that panel scopes its policy_fields read by `policy_id` and NOT by crop year —
  // so it returns 2025 rows beside 2026 ones. His unit 2|1 is LIVINGSTON on 13 of the 2025
  // rows and MONROE on 29 of the 2026 rows; pooled, it resolves to neither, and 13 of his
  // 61 appraisal lines fell back to the worksheet header. Scoped, all 61 place by the unit
  // and the counties read 1,155.5 / 377.8 — his SOI's own planted figures.
  //
  // ⚠ AND SCOPING IS CONDITIONAL, BECAUSE BORROWING ANOTHER YEAR'S MAP IS A SUPPORTED
  // CASE. The Claims Manager deliberately falls back to the whole field map when the
  // requested year has not been backfilled ("a 2025 claim check currently gets the 2026
  // fields") and labels it on screen. Scoping unconditionally would hand those farms an
  // EMPTY map and revert every appraisal to its header county — this defect by a new
  // route. So the year narrows the map only when the map actually holds that year.
  const wantYear = Number(cropYear);
  const fieldYears = new Set();
  for (const p of policyFields || []) {
    const y = Number(p && p.crop_year);
    if (Number.isFinite(y)) fieldYears.add(y);
  }
  const yearScoped = Number.isFinite(wantYear) && fieldYears.has(wantYear);
  // A row with NO crop year cannot be proved to belong to another one, so it is kept.
  const fields = yearScoped
    ? (policyFields || []).filter((p) => {
        const y = Number(p && p.crop_year);
        return !Number.isFinite(y) || y === wantYear;
      })
    : (policyFields || []);

  // The unit key — pfUnitKey / apprUnitKey — lives in unitKey.js, shared with
  // fieldCoverage's unit tier so the two can never disagree about which unit a row is on.
  // A unit number is digits; anything else is refused there and falls to the farm rung.

  const countyByUnit = new Map();
  const countyByFsn = new Map();
  for (const p of fields) {
    const f = String((p && p.fsn) ?? "").trim();
    const u = pfUnitKey(p);
    // ⚠ THE INSURANCE COUNTY, NOT THE FSA ADMINISTRATIVE ONE. `policy_fields.county` is
    // M13 P27 #18 — where the FSA FARM is filed — while `insured_county` is the COVERAGE's
    // county from P14, which is what the SOI prints and what a claim settles at. Placing an
    // appraisal by the FSA county is the same class of error as placing it by the
    // worksheet header: a real county, of the right farm, that the policy does not rate.
    //
    // Measured 2026-10-07 over CY2026: of 293 multi-county (farmer, crop) cells, **103**
    // disagree with crop_coverage when bucketed on `county` and **ZERO** disagree when
    // bucketed on `insured_county || county`. MULLIGAN FAMILY FARM is the case Kevin
    // raised — his field map reads LIVINGSTON 1,442.1 / MONROE 94.5 on the FSA column
    // against a coverage (and an NAU SOI) of 1,183.2 / 377.8, so ~259 acres of Monroe
    // appraisal were being filed under Livingston.
    //
    // The fallback is load-bearing: `insured_county || county`, never the bare column — a
    // NULL insured_county (rows written before sql/041) would otherwise collapse every such
    // field onto one candidate. See [[project_policy_fields_county_trap]].
    const c = up((p && p.insured_county) || (p && p.county));
    if (!c) continue;
    if (u) {
      const eu = countyByUnit.get(u) || new Set();
      eu.add(c);
      countyByUnit.set(u, eu);
    }
    if (f) {
      const e = countyByFsn.get(f) || new Set();
      e.add(c);
      countyByFsn.set(f, e);
    }
  }

  const byCounty = new Map();
  // Reported, never silent: a rollup that quietly re-files acres must be able to say
  // how many it moved and how many it could not place.
  const basis = { fromUnit: 0, fromField: 0, fromHeader: 0, moved: 0,
                  ambiguous: 0, unmatched: 0, unitUnusable: 0,
                  // Which field map answered, so a figure built on ANOTHER year's acreage
                  // is never silent. yearScoped false with a cropYear asked for means the
                  // map had no rows for it and an older year was borrowed.
                  yearScoped, fieldYears: [...fieldYears].sort(),
                  crossYearDropped: (policyFields || []).length - fields.length };
  for (const a of appraisals || []) {
    const header = up(a.county) || null;
    const f = String((a && a.fsn) ?? "").trim();
    // ⚠ RUNG 1 — the unit. A unit sits in one county, so where it resolves there is
    // nothing left to infer.
    const uk = apprUnitKey(a);
    if (!uk && String((a && a.unit_number) ?? "").trim()) basis.unitUnusable++;
    const uset = uk ? countyByUnit.get(uk) : null;
    let resolved = uset && uset.size === 1 ? [...uset][0] : null;
    const byUnit = resolved != null;
    // ⚠ RUNG 2 — the FSA farm, which places the rows whose unit column is unusable.
    const set = resolved == null && f ? countyByFsn.get(f) : null;
    if (resolved == null) resolved = set && set.size === 1 ? [...set][0] : null;
    if (resolved) {
      if (byUnit) basis.fromUnit++; else basis.fromField++;
      if (header && resolved !== header) basis.moved++;
    } else {
      // ⚠ RUNG 3 — the worksheet header, and the reason is recorded. A farm whose
      // insured ground spans counties is AMBIGUOUS rather than split on no evidence:
      // splitting it would move acres the record cannot attribute, which is this
      // defect running the other way.
      basis.fromHeader++;
      if ((uset && uset.size > 1) || (set && set.size > 1)) basis.ambiguous++;
      else if (f || uk) basis.unmatched++;
    }
    const k = resolved || header || "(county not stated)";
    const e = byCounty.get(k) || { county: k, acres: 0, qty: 0, lines: 0 };
    e.acres += n(a.appraised_acres) || 0;
    e.qty += n(a.appraised_bu) || n(a.appraised_qty) || 0;
    e.lines++;
    byCounty.set(k, e);
  }
  return { byCounty, basis };
}

function buildProductionEvidence({
  crop, cropYear, unit = "bu",
  coverages = [],      // crop_coverage
  policies = [],       // policies (for the crop-year scope)
  tickets = [],        // grain_delivery_tickets      — farm total, NO county
  jdFtf = [],          // jd_field_ftf, metric='yield' — HAS county
  policyFields = [],   // policy_fields — which CLUs are insured, FOR THIS CROP, this year
  appraisals = [],     // silage_appraisals           — has county
  countyRows = [],     // claim_county_production     — the typed/accepted split
  inputs = null,       // claim_check_inputs          — typed stored/fed/seed
  aphRows = [],        // production_history          — what the carrier already holds
} = {}) {
  const CROP = up(crop);
  const CY = Number(cropYear);
  const polIds = new Set(policies.filter((p) => Number(p.crop_year) === CY).map((p) => p.id));

  // ── WHAT IS INSURED, PER COUNTY ────────────────────────────────────────────
  // Counties come from the COVERAGE, so a county with coverage and no production still
  // appears and reads UNSETTLED rather than vanishing. countyProduction.js is explicit
  // about this and it is the half that makes a missing county visible.
  const counties = new Map();
  for (const c of coverages) {
    if (up(c.crop) !== CROP) continue;
    if (polIds.size && !polIds.has(c.policy_id)) continue;
    if (!isBasePlan(c.mpci_plan_code)) continue;
    const k = up(c.county) || "(county not stated)";
    const e = counties.get(k) || { county: k, insuredAcres: 0, ppAcres: 0, uiAcres: 0, rows: 0 };
    e.insuredAcres += n(c.acres) || 0;
    e.ppAcres += n(c.pp_acres) || 0;
    e.uiAcres += n(c.ui_acres) || 0;
    e.rows++;
    counties.set(k, e);
  }
  const insuredAcres = [...counties.values()].reduce((s, c) => s + c.insuredAcres, 0);

  // ── WHAT UNIT IS THIS CROP MEASURED IN, AND IS THERE ONE ANSWER? ────────────
  //
  // ⚠⚠ ACREAGE FOLDS; UNITS DO NOT. The caller passes a `unit` derived from the CROP NAME,
  // and for corn that is not enough: RMA insures corn grain as type 016 in BUSHELS and corn
  // silage as type 026 in TONS, on the same crop, often on the same policy. This repo has
  // already paid for mixing them — Lismore's Steuben corn read 86.5 "bu/ac" from
  // (154 bu + 18.9 ton) / 2 on $2.28M of liability, and `check-machine-vs-fsa` exists
  // because a footer summed machine x yield with no unit test across 43 of 172 farm-years.
  //
  // So the unit is DERIVED from the coverage rows' own type codes, and where they imply more
  // than one it is REFUSED rather than averaged or guessed. A production report stating tons
  // under a bushels heading is a wrong number on a document the grower certifies.
  //
  // ⚠ AND THIS IS NOT THEORETICAL — IT IS 40 FARMS. Measured over CY2026 base-plan coverage,
  // 2026-10-01: of 603 (farmer x crop) cells, **40 imply more than one unit, and every one is
  // CORN** carrying both grain 016 and silage 026. The largest are R L JEFFRES 6,540 ac,
  // LISMORE DAIRY 5,776, CENTRAL VALLEY GRAIN 2,812 and BAINBRIDGE 2,787. Corn is the biggest
  // crop in this book, so a single corn production figure would be wrong on all forty.
  //
  // ⚠ WHICH MEANS THE REFUSAL IS A STAGING POST, NOT THE ANSWER. A production report is filed
  // PER TYPE, so corn needs its evidence assembled per (crop, type) rather than per crop —
  // and the Claims Manager campaign is crop-level, so that is a dimension the whole surface
  // does not yet have. Refusing with the reason named is correct today and leaves the work
  // visible; silently picking one unit would hide it behind a plausible number.
  const typeUnits = new Set();
  for (const c of coverages) {
    if (up(c.crop) !== CROP) continue;
    if (polIds.size && !polIds.has(c.policy_id)) continue;
    if (!isBasePlan(c.mpci_plan_code)) continue;
    typeUnits.add(PR_UNIT(CROP, c.type_code));
  }
  const unitsSeen = [...typeUnits].filter(Boolean);
  // One answer, or none. `unit` falls back to what the caller said only when the coverage
  // rows cannot speak — never to break a tie.
  const resolvedUnit = unitsSeen.length === 1 ? unitsSeen[0] : (unitsSeen.length ? null : unit);
  const unitAmbiguous = unitsSeen.length > 1;

  // ── THE CHANNELS ───────────────────────────────────────────────────────────
  //
  // ⚠ A SCALE TICKET CANNOT SAY WHICH COUNTY THE BUSHELS CAME FROM. Measured over all 1,907
  // tickets: the columns are buyer, location and contract_ref — the ELEVATOR's location.
  // "There is no derivation hiding in the data." So tickets are a FARM TOTAL and are
  // reported as one; splitting them is a human act or a JD-derived suggestion.
  const tix = tickets.filter((t) => up(t.crop) === CROP && Number(t.crop_year) === CY);
  // The ELEVATOR's paid basis. Kept and SHOWN — it is what the grower's own settlement
  // sheets say, so a panel that printed only the RMA figure would read as disagreeing with
  // the paperwork in their hand. It is simply not what goes on the production report.
  const tixElevator = tix.length ? undo(tix.reduce((s, t) => s + cents(t.net_bushels), 0)) : null;
  const shrinkPerPt = inputs && n(inputs.shrink_per_pt) != null ? n(inputs.shrink_per_pt) : DEFAULT_SHRINK_PER_PT;
  const baseMoisture = inputs && n(inputs.base_moisture) != null ? n(inputs.base_moisture) : DEFAULT_BASE_MOISTURE;
  // ⚠ THE REPORTABLE QUANTITY. `rmaNetFromTickets` falls back to each load's elevator net
  // when that load carries no gross or no moisture, so a partial file degrades per TICKET
  // rather than per farm — and `shrinkableTix` says how many loads the shrink could reach,
  // because a figure derived from 3 of 21 loads is a different fact from one derived from 21.
  const tixBu = tix.length ? undo(cents(rmaNetFromTickets(tix, shrinkPerPt, baseMoisture))) : null;
  const shrinkableTix = tix.filter(ticketIsShrinkable).length;
  const tixMoist = tix.filter((t) => n(t.moisture) != null).length;

  // JD carries county, and its own refusals: a row with no county, no acres or no yield
  // contributes NOTHING — "a zero would read as a failed field rather than as a pass we
  // cannot value."
  // ⚠⚠ A PRODUCTION REPORT IS FILED AGAINST INSURED ACRES, AND THE MACHINE DOES NOT KNOW
  // WHICH THOSE ARE. `jd_field_ftf` attributes machine acres to CLUs by AREA OVERLAP, so
  // it carries every acre the combine drove over — custom work, a neighbour's ground, and
  // the grower's OWN ground that is insured to a different crop this year.
  //
  // Kevin, 2026-10-01, on Gary Swede's CY2026 wheat: *"I see they harvested more acres
  // than they had insured, are we counting acres harvested on a CLU that wasnt insured?"*
  // Measured that day — and the answer is yes, with a twist:
  //
  //   JD harvest acres                                        257.2
  //   insured wheat acres                                     232.7
  //   on CLUs carrying NO CY2026 WHEAT policy_field            31.7  (one 31.6 ac Livingston CLU)
  //   on insured CLUs only                                    225.6
  //
  // So the excess is not a neighbour's field — it is Swede's own ground, insured, but to
  // ANOTHER CROP. ⚠ THE SCOPE IS (farmer, crop, crop year), NOT the farmer alone: a first
  // measurement scoped only by farmer found 0.1 excess acres and was wrong, because a CLU
  // insured for corn is not insured ground for a wheat production report. Scoped properly
  // the machine covers 225.6 of 232.7 reported acres — slightly UNDER, which is the normal
  // direction and the shape that says the filter is right.
  //
  // ⚠ AND IT FAILS OPEN, DELIBERATELY. `policy_fields` is delete-and-reinsert per sync, so
  // a crop whose rows have not landed yet yields an EMPTY insured set — and scoping against
  // an empty set would exclude every acre and report the lane as 0 bu, which reads as a
  // total crop failure. That is the largest claim a policy can pay. So an empty scope means
  // UNSCOPED, and says so, rather than silently zeroing the evidence.
  const insuredClus = new Set();
  // Per county as well as overall: the monitor-yield rule below asks whether the combine
  // accounted for every insured field in ONE county, and a crop-wide answer cannot say that.
  const insuredCluByCounty = new Map();
  for (const p of policyFields) {
    if (Number(p.crop_year) !== CY) continue;
    if (up(p.crop).indexOf(CROP) < 0) continue;
    const id = String(p.clu_id || "").trim();
    if (id) {
      insuredClus.add(id.toUpperCase());
      const ck = up(p.county);
      if (ck) {
        const set = insuredCluByCounty.get(ck) || new Set();
        set.add(id.toUpperCase());
        insuredCluByCounty.set(ck, set);
      }
    }
  }
  const cluScoped = insuredClus.size > 0;
  const jdByCounty = new Map();
  let jdDropped = 0;
  // Counted, never silent: acres the machine ran that this crop does not insure, and acres
  // the attribution could not place at all. Those are DIFFERENT facts — one is ground we
  // can prove is out of scope, the other is ground we cannot prove anything about.
  let jdOffBookAcres = 0, jdOffBookRows = 0, jdNoCluAcres = 0, jdNoCluRows = 0;
  let jdNotGrainAcres = 0, jdNotGrainRows = 0;   // chopped / undeterminable passes on a bushel report
  for (const r of jdFtf) {
    if (up(r.crop).indexOf(CROP) < 0) continue;
    if (r.metric !== "yield") continue;      // seed_rate reaches 221,282 seeds/ac
    const y = r.operation_date ? new Date(r.operation_date).getUTCFullYear() : null;
    if (y !== CY) continue;
    const ac = n(r.acres), mv = n(r.mean_value), k = up(r.county);
    if (!k || !(ac > 0) || !(mv > 0)) { jdDropped++; continue; }
    // ⚠ A CHOPPED PASS IS NOT A GRAIN MONITOR FIGURE (Kevin, 2026-10-26 — see harvestForm.js).
    // `up(r.crop).indexOf("CORN")` also matches CORN SILAGE, so RED DAWN's 210 silage grids
    // (wet t/ha) were being summed as a corn monitor quantity in bushels. On a bushel report
    // only a COMBINED pass stands; a chopped or undeterminable one is set aside and COUNTED.
    // A tonnage report (insured silage) is outside Kevin's rule and is left as it was.
    if (String(unit || "").toLowerCase() === "bu" && harvestFormOf(r) !== "combined") {
      jdNotGrainAcres += ac; jdNotGrainRows++; continue;
    }
    if (cluScoped) {
      const clu = String(r.clu_id || "").trim().toUpperCase();
      // ⚠ NO CLU IS NOT THE SAME AS NOT INSURED. A row the attribution could not place is
      // UNKNOWN, and it is KEPT — dropping it would quietly shrink a grower's production
      // on the strength of our own geometry failing. It is reported instead.
      if (!clu) { jdNoCluAcres += ac; jdNoCluRows++; }
      else if (!insuredClus.has(clu)) { jdOffBookAcres += ac; jdOffBookRows++; continue; }
    }
    const e = jdByCounty.get(k) || { county: k, acres: 0, qty: 0, grids: 0, clus: new Set() };
    e.acres += ac; e.qty += ac * mv; e.grids++;
    { const cl = String(r.clu_id || "").trim().toUpperCase(); if (cl) e.clus.add(cl); }
    jdByCounty.set(k, e);
  }

  const apprAll = appraisals.filter((a) => up(a.crop) === CROP && Number(a.crop_year) === CY);
  // ⚠ A LINE ON SILAGE-INSURED ACRES IS NOT GRAIN PRODUCTION. NAU prints the coverage type on
  // every unit header and the reader stores it as `type_code`; a Type 026 line is a grain
  // bushel appraisal of acres insured as silage (Kevin 2026-11-02: "report it as is to match
  // how it's insured"). It is named on the lane, never added to it. A line with no code keeps
  // the reading it always had. Same rule as appraisalInsuredType in src/appraisalSheet.js.
  const appr = apprAll.filter((a) => !isSilageType(a.type_code));
  const apprSilageInsured = apprAll.filter((a) => isSilageType(a.type_code));

  /* ⚠ `silage_appraisals.county` IS THE WORKSHEET HEADER'S COUNTY, NOT THE FIELD'S.
   *
   * The adjuster's form carries ONE "County:" in its header and every parsed row
   * inherits it — see `parseHeader` in silageAppraisal.js, whose row builder sets
   * `county: d.county` from that single header match. A farm whose fields span two
   * counties therefore files every appraised acre under whichever county the
   * worksheet was headed with. It is not a parser defect; it is what the form says.
   *
   * MEASURED on MULLIGAN FAMILY FARM LLC, CY2026 corn, 2026-10-06: all 61 appraisal
   * rows read LIVINGSTON — including FSA farms 6116 (89.8 ac) and 6121 (4.7 ac),
   * which this panel's OWN by-farm table correctly places in MONROE because that
   * table joins the FSN to the insured fields. So the county table read
   * LIVINGSTON 1,533.3 appraised ac against 1,183.2 insured, and MONROE blank:
   * 94.5 acres and their bushels under the wrong county, on the very split a claim
   * settles at and production is reported at.
   *
   * ⚠ IT WAS INVISIBLE UNTIL THE ACRES WERE PRINTED. The bushel column alone had the
   * same misattribution and nothing to check it against — 298,043 bu on Livingston
   * looks no different from 203,000. Acres beside the insured acres they are a share
   * of is what made 130% readable.
   *
   * THE FSA FARM NUMBER IS ON THE APPRAISAL ROW, and `policy_fields` says which
   * county each farm's insured ground is in. Resolve from there, which is exactly
   * how the by-farm table does it, so the two surfaces cannot disagree.
   *
   * ⚠ A FARM SPANNING TWO COUNTIES RESOLVES TO NEITHER. The appraisal row names a
   * farm, not a field, so where that farm's insured ground is in more than one
   * county there is nothing on the row to choose between them — it keeps the header
   * county and is COUNTED, never silently split. Guessing would move acres on no
   * evidence, which is the error this fix exists to correct, running the other way.
   */
  const { byCounty: apprByCounty, basis: apprCounty } =
    appraisalsByCounty({ appraisals: appr, policyFields, cropYear: CY });

  // The typed (or JD-accepted) county split. countyIsStated is the nullable test: "" and 0
  // are kept distinct, because 0 is a real statement and "" is not.
  const stated = new Map();
  for (const r of countyRows) {
    if (up(r.crop) !== CROP || Number(r.crop_year) !== CY) continue;
    const k = up(r.county);
    if (!k) continue;
    stated.set(k, { county: k, bushels: n(r.bushels), uom: r.uom || unit, source: r.source || "manual" });
  }

  const typedStored = inputs ? n(inputs.stored_bu) : null;
  const typedFed = inputs ? n(inputs.fed_bu) : null;
  const typedSeed = inputs ? n(inputs.seed_bu) : null;

  // What the carrier already holds. A row with production > 0 means it HAS been reported.
  const aph = aphRows.filter((r) => up(r.crop) === CROP && Number(r.crop_year) === CY);
  const aphReported = aph.filter((r) => (n(r.production) || 0) > 0);
  const aphActual = aph.filter((r) => ["A", "PA"].includes(up(r.record_type)));

  // ── THE EVIDENCE LANES, EACH WITH ITS RMA CODE ─────────────────────────────
  const lanes = [];
  const lane = (kind, qty, detail) => {
    const m = PR.codeForEvidence(kind);
    lanes.push({
      kind, label: PR.EVIDENCE[kind].label,
      quantity: qty, unit: resolvedUnit,
      code: m.code, candidates: m.candidates, needsHuman: m.needsHuman, why: m.why,
      ...detail,
    });
  };
  if (tix.length) lane("scale_tickets", tixBu, {
    tickets: tix.length, withMoisture: tixMoist, county: null,
    // Both figures ride on the lane so the screen can print the pair and the factors that
    // separate them. A single number here, whichever basis it were, is unreadable: the
    // reader cannot tell a shrink from a data error.
    basis: "rma_net", elevatorNet: tixElevator, rmaNet: tixBu,
    shrinkPerPt, baseMoisture, shrinkable: shrinkableTix,
  });
  if (jdByCounty.size) lane("jd_yield", undo([...jdByCounty.values()].reduce((s, e) => s + cents(e.qty), 0)),
    { counties: [...jdByCounty.values()].map((e) => ({ county: e.county, acres: +e.acres.toFixed(1), quantity: +e.qty.toFixed(0), grids: e.grids })), dropped: jdDropped,
      // The scope and what it cost, ON the lane, so the screen can say why the machine
      // figure is smaller than the raw monitor total — and so an UNSCOPED lane cannot be
      // mistaken for a scoped one.
      cluScoped,
      offBookAcres: +jdOffBookAcres.toFixed(1), offBookRows: jdOffBookRows,
      notGrainAcres: +jdNotGrainAcres.toFixed(1), notGrainRows: jdNotGrainRows,
      noCluAcres: +jdNoCluAcres.toFixed(1), noCluRows: jdNoCluRows,
      insuredClus: insuredClus.size });
  if (apprByCounty.size) lane("appraisal", undo([...apprByCounty.values()].reduce((s, e) => s + cents(e.qty), 0)),
    { counties: [...apprByCounty.values()].map((e) => ({ county: e.county, acres: +e.acres.toFixed(1), quantity: +e.qty.toFixed(0), lines: e.lines })),
      // A rollup that re-files acres must be able to say so. `moved` is the count the
      // reader cares about — rows whose worksheet header disagreed with the county the
      // insured fields put them in — and ambiguous/unmatched are the rows it could NOT
      // place and left on the header. Silence here would make this fix indistinguishable
      // from the defect it replaces.
      countyBasis: { ...apprCounty },
      silageInsuredExcluded: {
        lines: apprSilageInsured.length,
        acres: +apprSilageInsured.reduce((s, a) => s + (n(a.appraised_acres) || 0), 0).toFixed(1),
        quantity: +apprSilageInsured.reduce((s, a) => s + (n(a.appraised_bu) || n(a.appraised_qty) || 0), 0).toFixed(0),
      } });
  if (typedStored != null) lane("bin_measurement", typedStored, { county: null, typed: true });
  if (typedFed != null) lane("fed_on_farm", typedFed, { county: null, typed: true });

  // ── DISAGREEMENTS: NAMED, NEVER RESOLVED ───────────────────────────────────
  const disagreements = [];
  const jdQty = jdByCounty.size ? undo([...jdByCounty.values()].reduce((s, e) => s + cents(e.qty), 0)) : null;
  const jdAcres = [...jdByCounty.values()].reduce((s, e) => s + e.acres, 0);
  if (tixBu != null && jdQty != null && tixBu > 0 && jdQty > 0) {
    const gap = Math.abs(tixBu - jdQty) / Math.max(tixBu, jdQty);
    if (gap > 0.10) {
      disagreements.push({
        between: ["scale_tickets", "jd_yield"],
        values: { scale_tickets: tixBu, jd_yield: jdQty },
        gapPct: +(gap * 100).toFixed(1),
        // Say the likely cause and label it as a guess. The repo's own measured failure
        // mode, not a theory invented here.
        note: "the two channels differ by more than 10%. A common cause is maps not yet "
            + "processed, which renders as FEWER ACRES rather than as missing — but that is "
            + "a guess, and neither figure is corrected here.",
      });
    }
  }
  if (jdAcres > 0 && insuredAcres > 0 && jdAcres > insuredAcres * 1.02) {
    disagreements.push({
      between: ["jd_yield", "insured_acres"],
      values: { jd_acres: +jdAcres.toFixed(1), insured_acres: +insuredAcres.toFixed(1) },
      note: "the machine ran more acres than are insured — custom work, or acres on another "
          + "insured's ground. A production report is filed against INSURED acres.",
    });
  }

  // ── IS EVERY INSURED ACRE ACCOUNTED FOR? ───────────────────────────────────
  //
  // ⚠ THIS IS A STATEMENT ABOUT COVERAGE, NOT ABOUT CORRECTNESS. It says whether each
  // insured county has SOME evidence behind it, never whether the figure is right. The
  // acre-level gate the FSA path uses ("mis-attributed acres are a real harm vector") is a
  // human reading this, not an arithmetic test.
  const perCounty = [...counties.values()].map((c) => {
    const jd = jdByCounty.get(c.county) || null;
    const ap = apprByCounty.get(c.county) || null;
    const st = stated.get(c.county) || null;
    const hasStated = !!st && st.bushels != null;
    return {
      county: c.county,
      insuredAcres: +c.insuredAcres.toFixed(1),
      ppAcres: +c.ppAcres.toFixed(1),
      // ⚠ PLANTED IS THE ACREAGE A PRODUCTION REPORT IS ABOUT. Kevin, 2026-10-07: *"instead
      // of insured acres, it should be planted acres. We dont care about Prevent Plant when
      // it comes to production reporting, just display planted acres."* He is right and it
      // is the same distinction this panel already draws everywhere else: no bushel was ever
      // grown on a prevent-planted acre, so showing INSURED beside an appraised figure
      // invites the reader to subtract and find a shortfall that is prevent plant.
      // MULLIGAN's LIVINGSTON is 1,183.2 insured, 27.7 PP, 1,155.5 planted — and his
      // adjuster walked exactly 1,155.5. Against insured that reads 27.7 ac short.
      plantedAcres: +Math.max(0, c.insuredAcres - c.ppAcres).toFixed(1),
      uiAcres: +c.uiAcres.toFixed(1),
      stated: hasStated ? st.bushels : null,
      statedSource: hasStated ? st.source : null,
      jd: jd ? { acres: +jd.acres.toFixed(1), quantity: +jd.qty.toFixed(0), grids: jd.grids } : null,
      // ⚠ MONITOR YIELD, AND THE DENOMINATOR IS THE MACHINE'S OWN ACRES — NOT INSURED ACRES.
      // Kevin, 2026-10-05: "it would be nice to see bushels per acre here by county if we
      // have machine data for everything."
      //
      // The JD quantity is the production off the acres the combine was MATCHED to, and CLU
      // attribution trims headlands and GPS edges — the sibling note on this very panel says
      // so ("John Deere's own field totals run a few percent higher again"). So the bushels
      // from those edges are missing from the numerator too, and dividing by the full insured
      // acreage mixes a trimmed numerator with an untrimmed denominator and reads LOW while
      // looking authoritative. Measured on HARTWAY FARMS CY2026 wheat: GENESEE 4,884 bu is
      // 83.6 bu/ac over the 58.4 ac the monitor covered and 79.5 over the 61.4 insured —
      // a 4.1 bu/ac gap on a figure an agent would read as the field's yield.
      //
      // It is therefore labelled a MONITOR yield, not production per insured acre. Those are
      // different claims and only one of them is what this number is.
      //
      // ⚠ AND IT IS WITHHELD UNLESS THE MACHINE ACCOUNTS FOR EVERY INSURED FIELD IN THIS
      // COUNTY. A yield computed over part of the ground is not the county's yield, and the
      // panel already exists to tell an agent what is still outstanding — printing an average
      // over the harvested half would answer the question it is supposed to raise. The test
      // is per COUNTY because one county can be complete while another is still appraising.
      // A county with no insured CLUs at all (an unkeyed coverage shell) is NOT complete:
      // the machine cannot have accounted for ground that is not keyed yet.
      ...(() => {
        const ins = insuredCluByCounty.get(c.county) || new Set();
        const seen = (jd && jd.clus) || new Set();
        const missing = [...ins].filter((x) => !seen.has(x));
        const complete = ins.size > 0 && missing.length === 0 && !!jd && jd.acres > 0;
        return {
          machineComplete: complete,
          machineMissingFields: ins.size ? missing.length : null,
          monitorYield: complete ? +(jd.qty / jd.acres).toFixed(1) : null,
          monitorYieldBasis: complete ? "monitor acres" : null,
        };
      })(),
      appraised: ap ? { acres: +ap.acres.toFixed(1), quantity: +ap.qty.toFixed(0), lines: ap.lines } : null,
      // ⚠ A COUNTY WITH NO INSURED ACRES IS AN UNKEYED COVERAGE SHELL, NOT A COUNTY WE ARE
      // MISSING PRODUCTION FOR. crop_coverage carries a row per (county x crop x plan) from the
      // moment the policy is written, and RMA keys acreage and liability later — so a county the
      // grower has no acres in this year sits here with acres NULL and liability NULL, which the
      // sum above turns into 0. Calling that UNSETTLED says we cannot speak for ground that does
      // not exist.
      //
      // Kevin, 2026-10-01, on BRIDGEWATER FARMING CY2026 wheat: "it says we dont have enough data
      // but I think we do for this customer". He was right. STEUBEN carries acres NULL, liability
      // NULL and ZERO policy_fields beside WILLIAMS' 735.6 insured acres, and it was blocking the
      // report. Measured book-wide CY2025+: of 317 multi-county (farmer, crop, year) cells, 181
      // carry at least one county with no insured acres (298 such counties) — and in 75 of them,
      // dropping those leaves exactly ONE real county, so the multi-county blocker vanishes
      // altogether. MARQUART BROS reads "4 of 5 counties have no stated production" against
      // ALLEGANY, LIVINGSTON, GENESEE and CATTARAUGUS, none of which have any wheat.
      //
      // It is still LISTED, because a coverage row genuinely exists and hiding it would be the
      // opposite error — it simply says what it is.
      // UNSETTLED is the honest word for a county we cannot speak for. Never 0.
      status: hasStated ? "stated" : (jd || ap) ? "evidence_but_unstated"
        : !(c.insuredAcres > 0) ? "no_acreage_keyed"
        : "unsettled",
    };
  }).sort((a, b) => b.insuredAcres - a.insuredAcres);

  const nCounty = perCounty.length;
  const statedCount = perCounty.filter((c) => c.status === "stated").length;

  // ── WHAT COULD BE FILLED, AND WHAT IS IN THE WAY ───────────────────────────
  const blockers = [];
  if (!insuredAcres) blockers.push("no insured acres on a base-plan coverage row for this crop and year");
  // ⚠ TWO UNITS ON ONE CROP BLOCKS THE REPORT. Corn grain is bushels and corn silage is
  // tons; a single figure covering both is neither, and Lismore read 86.5 "bu/ac" from
  // (154 bu + 18.9 ton) / 2 on $2.28M of liability. The split is real work — the report is
  // filed per type — so it is named rather than resolved here.
  if (unitAmbiguous) blockers.push(`this crop carries more than one unit of measure (${unitsSeen.join(" and ")}) — grain and silage are reported separately and cannot share a figure`);
  if (!lanes.length) blockers.push("no production evidence of any kind");
  // ⚠ ONLY A COUNTY WITH INSURED ACRES CAN BE MISSING PRODUCTION. A row with no acreage keyed
  // has no ground to report on, so counting it here blocks a report that is not actually short of
  // anything — 75 farm/crop/year cells in this book are blocked by exactly that and nothing else.
  // The zero-acre counties are still NAMED below rather than silently dropped, because "there is a
  // coverage row for a county you have no acres in" is worth a glance, just not a blocker.
  const reportable = perCounty.filter((c) => c.insuredAcres > 0);
  const nReportable = reportable.length;
  const statedReportable = reportable.filter((c) => c.status === "stated").length;
  if (nReportable > 1 && statedReportable < nReportable) {
    const missing = reportable.filter((c) => c.status !== "stated").map((c) => c.county);
    blockers.push(`multi-county: ${missing.length} of ${nReportable} counties have no stated production (${missing.join(", ")})`);
  }
  const unkeyed = perCounty.filter((c) => c.status === "no_acreage_keyed").map((c) => c.county);
  for (const l of lanes) if (l.needsHuman) {
    blockers.push(`${l.label}: the record type is ${l.candidates.join(" or ")} and only the grower can say which`);
  }
  if (disagreements.length) blockers.push(`${disagreements.length} channel disagreement(s) to resolve`);

  return {
    crop: CROP, cropYear: CY,
    // Counties whose coverage row exists but whose acreage RMA has not keyed. Reported, never
    // a blocker — see the note by `status`.
    unkeyedCounties: unkeyed,
    unit: resolvedUnit, unitAmbiguous, unitsSeen,
    insuredAcres: +insuredAcres.toFixed(1),
    counties: perCounty, nCounty, statedCount,
    lanes, disagreements, blockers,
    // Machine passes set aside because they were CHOPPED (or their form could not be told) on
    // a bushel report — at the top level, because when every pass is chopped there is no
    // jd_yield lane to carry the count, and silence would read as "no machine data".
    jdNotGrain: { acres: +jdNotGrainAcres.toFixed(1), rows: jdNotGrainRows },
    // What the carrier already holds, so a report that is already filed is not re-filed.
    carrier: {
      rows: aph.length, reportedRows: aphReported.length, actualRows: aphActual.length,
      alreadyReported: aphReported.length > 0,
      quantity: aphReported.length ? undo(aphReported.reduce((s, r) => s + cents(r.production), 0)) : null,
    },
    // `ready` is a statement that NOTHING IS IN THE WAY, never that the figures are right.
    ready: blockers.length === 0,
    recordTypeSource: PR.SOURCE,
  };
}

// ── WHAT THE RULES ABOVE ACTUALLY READ ───────────────────────────────────────
//
// ⚠ A COLUMN HAS TO BE SELECTED, NOT JUST ACCEPTED, and this module shipped with TWO that
// were not. The panel's `grain_delivery_tickets` select asked for `crop,crop_year,
// net_bushels,moisture` — no `gross_bushels` — so `rmaNetFromTickets` saw `undefined` on
// every load, fell back to the elevator net on all 21, and printed "+0" with "21 loads
// missing gross/moisture" against a book where ALL 1,907 tickets carry both. And
// `claim_check_inputs` asked for `stored_bu,fed_bu,seed_bu` only, so the typed shrink
// override was dead the moment it was written — the `associatedIifKey` defect, where a
// guard exists in source and its input never arrives.
//
// Neither was visible to `check-query-contract`: that checker reads `api/` only, and this
// panel lives in App.jsx. So the contract is DECLARED here and asserted by this module's own
// checker, which is the `bfrNeedsColumns` pattern. A rule added above that reads a new
// column must name it here or the checker cannot see it either.
const PRODUCTION_EVIDENCE_NEEDS = Object.freeze({
  grain_delivery_tickets: ["crop", "crop_year", "net_bushels", "gross_bushels", "moisture"],
  claim_check_inputs: ["crop", "crop_year", "stored_bu", "fed_bu", "seed_bu", "shrink_per_pt", "base_moisture"],
  crop_coverage: ["policy_id", "crop", "county", "acres", "mpci_plan_code", "type_code"],
  // clu_id is what scopes the machine acres to INSURED ground — see the jd lane.
  jd_field_ftf: ["county", "acres", "mean_value", "crop", "operation_date", "metric", "clu_id", "jd_operation_id"],
  // ⚠ `fsn` and `county` are READ BY THIS MODULE, not just by fieldCoverage.js — they are
  // what place an appraisal in a county instead of trusting the worksheet header. They were
  // missing from this list while the live select happened to carry them for buildFieldCoverage,
  // so the county fix worked by a neighbouring rule's needs rather than its own. Trimming that
  // select to match this list would have emptied countyByFsn, silently reverted every appraisal
  // to its header county, and left every checker green — the dead-guard shape check-query-contract
  // exists for.
  // ⚠ `unit_number` / `sub_unit` are RUNG 1 of the appraisal county ladder and place 77
  // of the book's 81 appraisal rows. Omitted from the select, countyByUnit is empty, every
  // row falls to the FSA farm, and Mulligan's Livingston reads 1,392.4 appraised acres on
  // 1,155.5 planted — with every checker green. The dead-guard shape exactly.
  policy_fields: ["clu_id", "crop", "crop_year", "fsn", "county", "insured_county",
                  "unit_number", "sub_unit"],
  silage_appraisals: ["crop", "crop_year", "county", "fsn", "unit_number", "type_code",
                      "appraised_acres", "appraised_bu", "appraised_qty"],
  claim_county_production: ["crop", "crop_year", "county", "bushels", "uom", "source"],
  production_history: ["crop", "crop_year", "county", "acres", "production", "record_type"],
});

module.exports = { buildProductionEvidence, appraisalsByCounty, isBasePlan, BASE_PLANS,
                   PRODUCTION_EVIDENCE_NEEDS, countyOverUnder, countyGuaranteeRead };
